import { Button, Form, Icon, Message, Divider, Input, Ref, Select } from "semantic-ui-react";
import React, { Component, createRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { observable } from "mobx";
import "../styles/TourIndexHeader.less";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import UserStore from "~/stores/UserStore";
import CustomerStore from "~/stores/CustomerStore";
import CountryStore from "~/stores/CountryStore";
import FormStore from "~/stores/FormStore";
import Address, { addressValidation } from "~/components/element/Address/Address";
import ModalError from "~/components/element/ModalError";
import ModalUnsavedConfirmation from "~/components/element/ModalUnsavedConfirmation";
import ChangePassword from "~/modules/ProfileSettings/components/ChangePassword";
import ImageUpload from "~/components/element/ImageUpload/ImageUpload";
import { validateEmail, getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import AssistantCcEmailList from "../components/AssistantEmails/AssistantCcEmailList";
import ContentLoader from "~/components/element/ContentLoader";
import QrCodeHelper from "~/utils/QrCodeHelper";
import StickyMenu from "~/components/StickyMenu";
import ConstantStore from "~/stores/ConstantStore";
import User from "~/models/User";
import ToastMessageStore from "~/stores/ToastMessageStore";
import ToastMessageContainer from "~/components/ToastMessageContainer";
import "../styles/ProfileAccount.less";

const validationRules = {
  ...addressValidation,
  username: {
    required: true,
  },
  firstName: {
    required: true,
  },
  lastName: {
    required: true,
  },
  email: {
    required: true,
    email: true,
  },
  contactPhone: {
    required: true,
  },
  assistantCcEmails: {
    test: (value, fail) => {
      if (typeof value === "undefined" || value === null) {
        return null;
      }

      const parts = value.split(/[\n]+/).filter(entry => entry.trim().length > 0);

      for (let i = 0; i < parts.length; i += 1) {
        const email = parts[i];

        if (validateEmail(email)) {
          return fail("invalid email");
        }
      }

      return null;
    },
  },
};

@observer
class CustomerAccountPage extends Component {
  @observable userStore = null;

  @observable customerStore = null;

  @observable countryStore = null;

  @observable formStore = null;

  ref = null;

  constructor(props) {
    super(props);
    this.ref = createRef();
    document.title = "Account Settings";
    this.userStore = new UserStore();
    this.customerStore = new CustomerStore();
    this.countryStore = new CountryStore();
    this.formStore = new FormStore();

    this.state = {
      openPasswordBox: false,
      addressResults: [],
      selectedAddressResult: null,
    };
  }

  componentDidMount() {
    const { showError, constantStore } = this.props;

    constantStore.load([ConstantStore.CONSTANTS.LOCALES]);
    this.countryStore.load();

    this.userStore.load().then(user => {
      const { userId } = user;

      return this.customerStore
        .load(userId)
        .then(() => {
          const { customer } = this.customerStore;
          customer.photoData = "";
          customer.logoData = "";
          this.bindForm(customer, user);
        })
        .catch(error => showError(error.message));
    });
  }

  onSubmit() {
    const { i18n, t, toastMessageStore } = this.props;
    const { model } = this.formStore;
    model.assistantCcEmails.trim("\n");
    const formCustomer = {
      firstName: model.firstName,
      lastName: model.lastName,
      title: model.title,
      agentLicenseNumber: model.agentLicenseNumber,
      contactPhone: model.contactPhone,
      contactPhone2: model.contactPhone2,
      designation: model.designation,
      email: model.email,
      website: model.website,
      disclaimer: model.disclaimer,
      company: model.company,
      assistantCcEmails: model.assistantCcEmails,
      locale: model.locale,
      enableCustomerPortal: model.enableCustomerPortal,
      address: {
        street: model.street,
        street2: model.street2,
        city: model.city,
        state: model.state,
        zip: model.zip,
        country: model.country,
      },
    };

    if (model.photoData !== "") {
      formCustomer.photoData = model.photoData;
    }

    if (model.logoData !== "") {
      formCustomer.logoData = model.logoData;
    }

    if (model.photoUrl === null) {
      formCustomer.photoUrl = model.photoUrl;
    }

    if (model.logoUrl === null) {
      formCustomer.logoUrl = model.logoUrl;
    }

    const formUser = {
      username: model.username,
    };

    return Promise.all([this.userStore.save(formUser), this.customerStore.save(formCustomer)]).then(() => {
      const { user } = this.userStore;
      const { customer } = this.customerStore;

      this.bindForm(customer, user);
      toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));

      // At the save we need to change the locale
      if (customer.locale !== null) {
        i18n.changeLanguage(customer.locale);
      } else {
        i18n.changeLanguage(customer.providerDefaults.locale);
      }
    });
  }

  bindForm(customer, user) {
    const { address } = customer;
    const { username } = user;
    const initialModel = {
      ...address,
      ...customer,
      inline: address.inline,
      username,
    };

    this.formStore = new FormStore(initialModel);
  }

  openChangePassword(open) {
    if (open === true) {
      this.setState({ openPasswordBox: true });
    }

    if (open === false) {
      this.setState({ openPasswordBox: false });
    }
  }

  checkPhoto(e, data) {
    const { setProperty } = this.formStore;
    if (data.checked === true) {
      setProperty("photoUrl", null);
    }
  }

  checkLogo(e, data) {
    const { setProperty } = this.formStore;
    if (data.checked === true) {
      setProperty("logoUrl", null);
    }
  }

  render() {
    const { t, constantStore, toastMessageStore } = this.props;
    const { isLoading, isSaving, customer, errorMessages: customerServerErrors } = this.customerStore;
    const { openPasswordBox, selectedAddressResult, addressResults } = this.state;
    const { model, bindInput, setModel, setProperty, isDirty } = this.formStore;
    const { countries, loading: countryLoading } = this.countryStore;
    const { errorMessages: userErrors, loading: userLoading, user } = this.userStore;
    const schema = getValidationErrors(validationRules, model, { countries });

    const serverValidationErrors = { ...customerServerErrors, ...userErrors };

    if (!customer.providerDefaults || isLoading || userLoading || countryLoading || constantStore.isLoading) {
      return <ContentLoader />;
    }

    const locales = constantStore.getList(ConstantStore.CONSTANTS.LOCALES);
    const localeOptions = constantStore.getFormList(ConstantStore.CONSTANTS.LOCALES);

    const toursPageHref = `http://${customer.canonicalDomain}/customer-portal/${customer.customerId}/tours`;

    const customerPhotoUrl =
      customer.photoUrl !== null ? `${customer.photoPathWeb}` : "/tourbuilder/assets/images/user_sil.jpg";
    const customerLogoUrl =
      customer.logoUrl !== null ? `${customer.logoPathWeb}` : "/tourbuilder/assets/images/logo_goes_here.jpg";

    return (
      <>
        <ModalUnsavedConfirmation
          when={isDirty}
          onSave={() => this.onSubmit()}
          onDiscard={() => this.bindForm(customer, user)}
        />
        <LoadingWrapper loading={isSaving} message={t("saving")} />
        <ModalError nestStore={this.customerStore} />

        <div className="profile-account-outer-container">
          <div className="profile-account-header">
            <div className="profile-account-tours-link-wrap">
              <p className="profile-account-tours-link-p">{t("clientPanelInfo:linkCustomerPortal")}</p>
              <div className="profile-account-tours-link-list">
                <Button
                  size="mini"
                  floated="right"
                  icon
                  as="a"
                  href={`/customer-portal/${customer.customerId}/tours`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="linkify" /> {t("webLink")}
                </Button>
                <Button
                  size="mini"
                  icon
                  floated="right"
                  as="a"
                  href={`/customer-portal/${customer.customerId}/tours?format=rss`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="rss square" /> {t("RSSFeed")}
                </Button>
                <Button
                  size="mini"
                  icon
                  as="a"
                  floated="right"
                  href={QrCodeHelper.makeQueryQrCode(toursPageHref)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="qrcode" /> {t("QR")}
                </Button>
              </div>
            </div>

            <div className="profile-account-h1-wrap">
              <h1>{t("profileSettings")}</h1>
            </div>
          </div>

          {!schema.isValid ? <Message negative>{t("clientPanelInfo:makeSureRequiredFieldsFilled")}</Message> : null}

          <Ref innerRef={this.ref}>
            <div>
              <StickyMenu
                contextRef={this.ref}
                hideHeaderWhenIsNotSticky
                headerText={t("profileSettings")}
                button={
                  <div className="profile-account-save-btn-wrap">
                    <Button primary icon disabled={!isDirty || !schema.isValid} onClick={() => this.onSubmit()}>
                      <Icon name="save" />
                      {t("saveChanges")}
                    </Button>
                  </div>
                }
              />

              <Form className="profile-account-form-wrap">
                <h4>{t("clientPanelInfo:usernamePassword")}</h4>
                <div className="profile-account-container">
                  <div className="profile-account-input">
                    <Form.Field
                      control={Input}
                      label={t("clientPanelInfo:editUsername")}
                      {...bindInput("username", schema, customerServerErrors)}
                    />
                    {schema.fields.username && !schema.fields.username.isValid && (
                      <Message negative>{t("clientPanelInfo:usernameRequired")}</Message>
                    )}
                    {serverValidationErrors.username ? (
                      <Message negative>{t("clientPanelInfo:usernameInvalidTryOther")}</Message>
                    ) : null}
                  </div>

                  <div />
                  <div>
                    {openPasswordBox === false ? (
                      <>
                        <Form.Field className="customer-password-label" label={t("clientPanelInfo:changePassword")} />
                        <Button
                          className="customer-password-btn"
                          icon
                          primary
                          onClick={() => this.openChangePassword(true)}
                        >
                          <Icon name="key" />
                          {t("clientPanelInfo:changeYourPassword")}
                        </Button>
                      </>
                    ) : null}

                    {openPasswordBox ? (
                      <>
                        <ChangePassword userStore={this.userStore} close={() => this.openChangePassword(false)} />
                      </>
                    ) : null}
                  </div>
                </div>

                <Divider className="profile-account-divider" />

                <h4>{t("clientPanelInfo:personalInformation")}</h4>
                <div className="profile-account-container">
                  <div className="profile-account-input">
                    <Form.Field
                      control={Input}
                      label={t("clientPanelInfo:firstName")}
                      {...bindInput("firstName", schema, customerServerErrors)}
                    />
                    {schema.fields.firstName && !schema.fields.firstName.isValid && (
                      <Message negative>{t("clientPanelInfo:firstNameRequired")}</Message>
                    )}
                  </div>
                  <div className="profile-account-input">
                    <Form.Field
                      control={Input}
                      label={t("clientPanelInfo:lastName")}
                      {...bindInput("lastName", schema, customerServerErrors)}
                    />
                    {schema.fields.lastName && !schema.fields.lastName.isValid && (
                      <Message negative>{t("clientPanelInfo:lastNameRequired")}</Message>
                    )}
                  </div>
                  <div className="profile-account-input">
                    <Form.Field
                      control={Input}
                      label={t("clientPanelInfo:title")}
                      {...bindInput("title", schema, customerServerErrors)}
                    />
                  </div>
                  <div className="profile-account-input">
                    <Form.Field
                      control={Input}
                      label={t("clientPanelInfo:agentLicenseNumber")}
                      {...bindInput("agentLicenseNumber", schema, customerServerErrors)}
                    />
                  </div>
                  <div className="profile-account-input">
                    <Form.Field
                      control={Input}
                      label={t("clientPanelInfo:primaryPhone")}
                      {...bindInput("contactPhone", schema, customerServerErrors)}
                    />
                    {schema.fields.contactPhone && !schema.fields.contactPhone.isValid && (
                      <Message negative>{t("clientPanelInfo:contactPhoneCannotBlank")}</Message>
                    )}
                  </div>
                  <div className="profile-account-input">
                    <Form.Field
                      control={Input}
                      label={t("clientPanelInfo:secondaryPhone")}
                      {...bindInput("contactPhone2", schema, customerServerErrors)}
                    />
                  </div>
                  <div className="profile-account-input">
                    <Form.Field
                      control={Input}
                      label={t("clientPanelInfo:designationsCertifications")}
                      {...bindInput("designation", schema, customerServerErrors)}
                    />
                  </div>
                  <div className="profile-account-input">
                    <Form.Field
                      control={Input}
                      label={t("email")}
                      {...bindInput("email", schema, customerServerErrors)}
                    />
                    {schema.fields.email && !schema.fields.email.isValid && (
                      <Message negative>{t("clientPanelInfo:validEmailRequired")}</Message>
                    )}
                  </div>
                  <div className="profile-account-input">
                    <Form.Field
                      control={Input}
                      label={t("website")}
                      {...bindInput("website", schema, customerServerErrors)}
                    />
                  </div>
                  <div className="profile-account-input">
                    <Form.Field
                      className="select-with-default-value-null"
                      control={Select}
                      label={t("locale")}
                      value={model.locale}
                      placeholder={`${t("clientPanelConstants:useProviderSetting")}: ${t(
                        locales[customer.providerDefaults.locale].toLowerCase(),
                      )}`}
                      options={[
                        {
                          key: "default",
                          value: null,
                          text: `${t("clientPanelConstants:useProviderSetting")}: ${t(
                            locales[customer.providerDefaults.locale].toLowerCase(),
                          )}`,
                        },
                      ].concat(localeOptions)}
                      onChange={(_event, { value }) => setProperty("locale", value)}
                    />
                  </div>
                  <div className="profile-account-input">
                    <Form.TextArea label={t("disclaimer")} {...bindInput("disclaimer", schema, customerServerErrors)} />
                  </div>
                </div>
                <Divider className="profile-account-divider" />

                <div className="customer-photo-logo-container">
                  <div className="customer-photo-wrap">
                    <h4>{t("clientPanelInfo:profilePhoto")}</h4>
                    {customer.photoUrl !== null ? (
                      <Form.Checkbox
                        className="customer-img-delete"
                        label={t("clientPanelInfo:clickDelete")}
                        onChange={(event, data) => this.checkPhoto(event, data)}
                      />
                    ) : (
                      <p>{t("clientPanelInfo:clickAddPhotoToProfile")}</p>
                    )}

                    <div className="customer-profile-photo-img-wrap">
                      <ImageUpload
                        onFileSelected={encoded => setProperty("photoData", encoded)}
                        defaultSrc={model.photoData || customerPhotoUrl}
                      />
                    </div>
                  </div>

                  <Divider className="profile-account-divider photo-logo-divider" />

                  <div className="customer-logo-wrap">
                    <h4>{t("clientPanelInfo:companyLogo")}</h4>
                    {customer.logoUrl !== null ? (
                      <Form.Checkbox
                        className="customer-img-delete"
                        label={t("clientPanelInfo:clickDelete")}
                        onChange={(event, data) => this.checkLogo(event, data)}
                      />
                    ) : (
                      <p>{t("clientPanelInfo:clickAddLogoToCompany")}</p>
                    )}
                    <div className="customer-profile-photo-img-wrap">
                      <ImageUpload
                        onFileSelected={encoded => setProperty("logoData", encoded)}
                        defaultSrc={model.logoData || customerLogoUrl}
                      />
                    </div>
                  </div>
                </div>
                <Divider className="profile-account-divider" />

                <h4>{t("clientPanelInfo:companyInformation")}</h4>
                <div className="profile-account-container">
                  <div className="profile-account-input">
                    <Form.Field
                      control={Input}
                      label={t("clientPanelInfo:company")}
                      {...bindInput("company", schema, customerServerErrors)}
                    />
                  </div>
                  <Address
                    countries={countries}
                    model={model}
                    isDirty={isDirty}
                    setModel={setModel}
                    bindInput={bindInput}
                    schema={schema}
                    serverValidationErrors={customerServerErrors}
                    hasSelectedResult={!!selectedAddressResult}
                    addressResults={addressResults}
                    setAddressResults={results => this.setState({ addressResults: results })}
                    setSelectedAddressResult={result => this.setState({ selectedAddressResult: result })}
                    useTranslation
                  />
                </div>
                <Divider className="profile-account-divider" />

                <h4>{t("clientPanelInfo:assistants")}</h4>
                <Form.Group className="profile-account-container customer-assistants-wrap">
                  <AssistantCcEmailList
                    emails={model.assistantCcEmails ? model.assistantCcEmails.split(/[\n]/) : []}
                    onChange={emails => setProperty("assistantCcEmails", emails.join("\n"))}
                  />
                </Form.Group>
                {schema.fields.assistantCcEmails && !schema.fields.assistantCcEmails.isValid && (
                  <Message negative>{t("clientPanelInfo:oneMoreEmailsInvalid")}</Message>
                )}

                <Divider className="profile-account-divider" />

                <h4>{t("customerPortal:customerPortal")}</h4>
                <div className="profile-account-container">
                  <div className="profile-account-input">
                    <Form.Checkbox
                      label={t("customerPortal:enableCustomerPortal")}
                      checked={model.enableCustomerPortal}
                      onChange={(event, data) => setProperty("enableCustomerPortal", data.checked)}
                    />
                  </div>
                </div>
              </Form>
            </div>
          </Ref>
        </div>
        <ToastMessageContainer toastMessageStore={toastMessageStore} />
      </>
    );
  }
}

CustomerAccountPage.propTypes = {
  constantStore: PropTypes.instanceOf(ConstantStore),
  user: PropTypes.instanceOf(User),
  showError: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
};

export default withTranslation()(ErrorWrapper(CustomerAccountPage));
